import React, { useMemo, useState, useEffect } from 'react';
import { Card, Descriptions, Select, Table, Pagination, Tag, Button, message, Modal, Form, DatePicker, Input, InputNumber, Space, Switch } from 'antd';
import { PlusOutlined, HistoryOutlined } from '@ant-design/icons';
import { GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, type AllSettlementClientResponse } from './graphql/queries/getAllSettlementClient';
import { settlementClientRoleTypeOptions, SettlementClientRoleType, type SettlementClientRoleTypeValue } from './config/settlementClientRoleType';
import { formatTimeWithTimezone } from '../../../utils/timezoneFormatter';
import { gql, useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { REPLENISH_BILLING_PERIOD_INSTANCES, DELETE_BILLING_PERIOD_INSTANCE, REGENERATE_BILLING_PERIOD_INSTANCE, CREATE_HISTORICAL_PERIOD_INSTANCE, CREATE_BILLING_PERIOD_RULE_VERSION } from '../../../graphql/mutations/settlement/settlementBillMutations';
import { BillDetailModal } from '../../../components/settlement/BillDetailModal';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

// 周期类型：后端 Int code 与 GraphQL 枚举名对应（BillCycleConfigurationEnum 0~4）
const CYCLE_TYPE_OPTIONS = [
  { code: 0, value: 'NATURE_DAY', label: '自然日' },
  { code: 1, value: 'NATURE_WEEK', label: '自然周' },
  { code: 2, value: 'NATURE_MONTH', label: '自然月' },
  { code: 3, value: 'HALF_MONTH', label: '半月' },
  { code: 4, value: 'NATURE_BIWEEKLY', label: '双周' },
];

// 账期实例状态：与后端 BillingPeriodInstanceStatus 对应（删除实例即置为 3 作废）
const INSTANCE_STATUS_TAG: Record<number, { label: string; color: string }> = {
  0: { label: '未生效', color: 'default' },
  1: { label: '生效', color: 'green' },
  2: { label: '已关闭', color: 'blue' },
  3: { label: '作废', color: 'red' },
};

const GET_ACTIVE_RULE = gql`
  query GetActiveBillingPeriodRule($clientId: Long!, $billType: BillTypeEnum!) {
    getActiveBillingPeriodRule(clientId: $clientId, billType: $billType) {
      id
      version
      cycleType
      closeOffsetDays
      closeTimeOfDay
      issueOffsetDays
      issueTimeOfDay
      summaryOffsetDays
      summaryTimeOfDay
      timeZone
      carryOverFlag
      exclusionLogic
      status
      createdAt
      updatedAt
    }
  }
`;

const GET_ALL_RULES = gql`
  query GetBillingPeriodRulesByClient($clientId: Long!, $billType: BillTypeEnum) {
    getBillingPeriodRulesByClient(clientId: $clientId, billType: $billType) {
      id
      version
      cycleType
      closeOffsetDays
      closeTimeOfDay
      issueOffsetDays
      issueTimeOfDay
      summaryOffsetDays
      summaryTimeOfDay
      timeZone
      carryOverFlag
      exclusionLogic
      status
      createdAt
      updatedAt
    }
  }
`;

const PREVIEW_PERIOD_INSTANCE = gql`
  query PreviewPeriodInstance($ruleId: Long!, $periodStartTime: String!) {
    previewPeriodInstance(ruleId: $ruleId, periodStartTime: $periodStartTime) {
      id
      ruleId
      settlementClientId
      billType
      version
      periodStart
      periodEnd
      closeTime
      issueTime
      summaryTime
      timeZone
      carryOverFlag
      exclusionLogic
      status
      isIssued
    }
  }
`;

const GET_INSTANCES_BY_RULE = gql`
  query GetBillingPeriodInstancesByRule($ruleId: Long!, $pageRequest: PageRequestInput) {
    getBillingPeriodInstancesByRule(ruleId: $ruleId, pageRequest: $pageRequest) {
      content {
        id
        periodStart
        periodEnd
        closeTime
        summaryTime
        timeZone
        status
        isIssued
        createdAt
      }
      totalElements
      totalPages
      currentPage
      pageSize
      hasNext
      hasPrevious
    }
  }
`;

const BillingPeriodOverviewPage: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  const [clientId, setClientId] = useState<string | undefined>();
  const [clientRoleType, setClientRoleType] = useState<SettlementClientRoleTypeValue>(SettlementClientRoleType.CHANNEL_CLIENT);
  const [billType, setBillType] = useState<string | undefined>('DELIVERY_FEE_BILL');
  const [page, setPage] = useState(1);
  const [viewRuleId, setViewRuleId] = useState<string | undefined>(); // 当前查看的规则版本，未选则回落到 ACTIVE 规则
  const pageSize = 10;

  // 弹窗相关状态
  const [billModalVisible, setBillModalVisible] = useState(false);
  const [selectedPeriodInstanceId, setSelectedPeriodInstanceId] = useState<string | null>(null);

  // 添加历史账期相关状态
  const [addHistoricalModalVisible, setAddHistoricalModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedRuleId, setSelectedRuleId] = useState<string | undefined>();
  const [selectedStartTime, setSelectedStartTime] = useState<Dayjs | null>(null);
  const [isCreatingHistorical, setIsCreatingHistorical] = useState(false);
  const [previewDataResetKey, setPreviewDataResetKey] = useState(0); // 用于重置预览数据

  // 新增账期规则版本相关状态
  const [ruleVersionModalVisible, setRuleVersionModalVisible] = useState(false);
  const [ruleVersionForm] = Form.useForm();
  const [isCreatingRuleVersion, setIsCreatingRuleVersion] = useState(false);

  const { data: ruleData, refetch: refetchRule } = useQuery(GET_ACTIVE_RULE, {
    variables: {
      clientId: clientId ?? null,
      billType: billType
    },
    skip: !clientId || !billType,
    fetchPolicy: 'cache-and-network'
  });

  const rule = ruleData?.getActiveBillingPeriodRule;

  const { data: allRulesData, refetch: refetchAllRules } = useQuery(GET_ALL_RULES, {
    variables: {
      clientId: clientId ?? null,
      billType: billType
    },
    skip: !clientId || !billType,
    fetchPolicy: 'cache-and-network'
  });

  const allRules = useMemo(() => allRulesData?.getBillingPeriodRulesByClient || [], [allRulesData]);

  const sortedRules = useMemo(
    () => [...allRules].sort((a: any, b: any) => b.version - a.version),
    [allRules]
  );
  const viewRule = useMemo(
    () => allRules.find((r: any) => String(r.id) === String(viewRuleId)) ?? rule,
    [allRules, viewRuleId, rule]
  );
  const viewRuleIsActive = !!viewRule && viewRule.status === 1 && viewRule.id === rule?.id;

  const [previewPeriodInstance, { data: previewData, loading: previewLoading }] = useLazyQuery(PREVIEW_PERIOD_INSTANCE, {
    fetchPolicy: 'network-only'
  });

  // 只有在弹窗打开且有选中规则和时间时才显示预览数据
  const previewInstance = addHistoricalModalVisible && previewDataResetKey > 0 ? previewData?.previewPeriodInstance : undefined;

  const { data: instancesData, refetch: refetchInstances } = useQuery(GET_INSTANCES_BY_RULE, {
    variables: {
      ruleId: viewRule?.id,
      pageRequest: {
        page: page,
        pageSize: pageSize
      }
    },
    skip: !viewRule?.id,
    fetchPolicy: 'cache-and-network'
  });

  // 刷新账期实例变更
  const [replenishBillingPeriodInstances] = useMutation(REPLENISH_BILLING_PERIOD_INSTANCES);
  const [deleteBillingPeriodInstance] = useMutation(DELETE_BILLING_PERIOD_INSTANCE);
  const [regenerateBillingPeriodInstance] = useMutation(REGENERATE_BILLING_PERIOD_INSTANCE);
  const [createHistoricalPeriodInstance] = useMutation(CREATE_HISTORICAL_PERIOD_INSTANCE);
  const [createBillingPeriodRuleVersion] = useMutation(CREATE_BILLING_PERIOD_RULE_VERSION);

  const instances = useMemo(() => instancesData?.getBillingPeriodInstancesByRule?.content || [], [instancesData]);
  const paginationInfo = instancesData?.getBillingPeriodInstancesByRule;

  const { data: settlementClientData, loading: settlementClientLoading } = useQuery<AllSettlementClientResponse>(
    GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS,
    {
      variables: { roleType: clientRoleType },
      fetchPolicy: 'cache-and-network'
    }
  );

  const settlementClientOptions = settlementClientData?.allOptions || [];
  const clientLabel = clientRoleType === SettlementClientRoleType.DSP_CLIENT ? 'DSP' : '客户(channel)';

  // 获取选中规则的时区
  const selectedRule = useMemo(() => {
    return allRules.find((r: any) => r.id === selectedRuleId);
  }, [allRules, selectedRuleId]);

  // 处理生成账期规则
  const handleGenerateBillingPeriod = async () => {
    if (!clientId || !billType) {
      message.warning('请先选择客户和账单类型');
      return;
    }

    if (!rule || !rule.id) {
      message.warning('该客户和账单类型暂无账期规则，请先创建账期规则');
      return;
    }

    Modal.confirm({
      title: '刷新账期实例',
      content: `确定要根据当前账期规则（版本 ${rule.version}）刷新账期实例吗？系统将自动补齐未来的账期实例。未来实例数包含其它规则版本尚未结束的实例，旧版本未来实例足够时会提示实例充足。`,
      okText: '确认刷新',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await replenishBillingPeriodInstances({
            variables: {
              ruleId: rule.id
            }
          });

          if (result.data?.replenishBillingPeriodInstances?.success) {
            const msg = result.data.replenishBillingPeriodInstances.message;
            message.success(msg);

            // 刷新页面数据
            if (viewRule?.id) {
              refetchInstances({ ruleId: viewRule.id });
            }
          } else {
            message.error(result.data?.replenishBillingPeriodInstances?.message || '账期实例刷新失败');
          }
        } catch (error) {
          message.error('账期实例刷新失败: ' + (error as Error).message);
        }
      }
    });
  };

  // 处理删除账期实例
  const handleDeleteInstance = async (instanceId: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个账期实例吗？删除后无法恢复。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await deleteBillingPeriodInstance({
            variables: { instanceId }
          });

          if (result.data?.deleteBillingPeriodInstance?.success) {
            message.success('账期实例删除成功');
            // 刷新页面数据
            if (viewRule?.id) {
              refetchInstances({ ruleId: viewRule.id });
            }
          } else {
            message.error(result.data?.deleteBillingPeriodInstance?.message || '账期实例删除失败');
          }
        } catch (error) {
          message.error('账期实例删除失败: ' + (error as Error).message);
        }
      }
    });
  };

  // 处理重生成账期实例
  const handleRegenerateInstance = async (instanceId: string) => {
    Modal.confirm({
      title: '确认重生成',
      content: '确定要重新生成这个账期实例吗？原实例将被删除，新实例将重新创建。',
      okText: '确认重生成',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await regenerateBillingPeriodInstance({
            variables: { instanceId }
          });

          if (result.data?.regenerateBillingPeriodInstance?.success) {
            message.success('账期实例重生成成功');
            // 刷新页面数据
            if (viewRule?.id) {
              refetchInstances({ ruleId: viewRule.id });
            }
          } else {
            message.error(result.data?.regenerateBillingPeriodInstance?.message || '账期实例重生成失败');
          }
        } catch (error) {
          message.error('账期实例重生成失败: ' + (error as Error).message);
        }
      }
    });
  };

  // 处理点击账期实例查看账单
  const handleViewBill = (periodInstanceId: string) => {
    setSelectedPeriodInstanceId(periodInstanceId);
    setBillModalVisible(true);
  };

  // 关闭账单弹窗
  const handleCloseBillModal = () => {
    setBillModalVisible(false);
    setSelectedPeriodInstanceId(null);
  };

  // 打开新增账期规则版本弹窗（以当前最新规则预填）
  const handleOpenRuleVersion = () => {
    ruleVersionForm.setFieldsValue(rule ? {
      cycleType: CYCLE_TYPE_OPTIONS.find((o) => o.code === rule.cycleType)?.value ?? 'NATURE_MONTH',
      closeOffsetDays: rule.closeOffsetDays, closeTimeOfDay: rule.closeTimeOfDay,
      summaryOffsetDays: rule.summaryOffsetDays, summaryTimeOfDay: rule.summaryTimeOfDay,
      issueOffsetDays: rule.issueOffsetDays, issueTimeOfDay: rule.issueTimeOfDay,
      timeZoneId: rule.timeZone, carryOver: rule.carryOverFlag === 1,
    } : {
      cycleType: 'NATURE_MONTH',
      closeOffsetDays: 0, closeTimeOfDay: '23:59:59',
      summaryOffsetDays: 1, summaryTimeOfDay: '04:00:00',
      issueOffsetDays: 1, issueTimeOfDay: '04:00:00',
      timeZoneId: undefined, carryOver: true,
    });
    setRuleVersionModalVisible(true);
  };

  const handleSubmitRuleVersion = async () => {
    let values: Record<string, unknown>;
    try {
      values = await ruleVersionForm.validateFields();
    } catch {
      // 校验失败时 antd 已在表单上提示，无需额外处理
      return;
    }
    Modal.confirm({
      title: '确认新增账期规则版本',
      content: '旧版本规则将失效，其已生成的账期实例保留不动。新版本的实例会接在所有版本最后一个未结束的实例之后生成：只要旧版本还有足够的未来实例（月结 ≥ 2 个，含当前月），「刷新账期实例」会提示实例充足。若要新版本提前生效，请先在「规则版本」中切到旧版本删除其未来实例，再切回新版本刷新；过去账期请用「添加历史账期」补建。',
      okText: '确认新增',
      cancelText: '取消',
      onOk: async () => {
        if (isCreatingRuleVersion) return;
        setIsCreatingRuleVersion(true);
        try {
          const result = await createBillingPeriodRuleVersion({
            variables: {
              input: {
                clientId: clientId!,
                billType,
                ...values,
                timeZoneId: values.timeZoneId || null,
              },
            },
          });
          const payload = result.data?.createBillingPeriodRuleVersion;
          if (payload?.success) {
            message.success(payload.message);
            setRuleVersionModalVisible(false);
            setViewRuleId(payload.ruleId ? String(payload.ruleId) : undefined);
            setPage(1);
            refetchRule();
            refetchAllRules();
          } else {
            message.error(payload?.message || '新增账期规则版本失败');
          }
        } catch (error) {
          message.error('新增账期规则版本失败: ' + (error as Error).message);
        } finally {
          setIsCreatingRuleVersion(false);
        }
      },
    });
  };

  // 打开添加历史账期弹窗
  const handleOpenAddHistorical = () => {
    if (!clientId || !billType) {
      message.warning('请先选择客户和账单类型');
      return;
    }
    setAddHistoricalModalVisible(true);
    form.resetFields();
    setSelectedRuleId(undefined);
    setSelectedStartTime(null);
    setIsCreatingHistorical(false);
    setPreviewDataResetKey(0); // 重置预览数据
  };

  // 关闭添加历史账期弹窗
  const handleCloseAddHistorical = () => {
    setAddHistoricalModalVisible(false);
    form.resetFields();
    setSelectedRuleId(undefined);
    setSelectedStartTime(null);
    setIsCreatingHistorical(false);
    setPreviewDataResetKey(0); // 重置预览数据
  };

  // 当客户或账单类型切换时，重置预览数据
  useEffect(() => {
    if (!addHistoricalModalVisible) {
      setPreviewDataResetKey(0);
      setSelectedRuleId(undefined);
      setSelectedStartTime(null);
    }
  }, [clientId, billType, addHistoricalModalVisible]);

  // 预览账期实例
  useEffect(() => {
    // 只有在弹窗打开时才进行预览
    if (addHistoricalModalVisible && selectedRuleId && selectedStartTime && selectedRule) {
      // 将用户选择的时间理解为规则时区的本地时间，然后转换为 UTC 的 ISO 字符串
      // selectedStartTime 是本地时区的 dayjs 对象，需要先设置为规则时区，再转换为 UTC
      const startTimeInRuleTimezone = selectedStartTime.tz(selectedRule.timeZone, true); // true 表示保持时间值不变，只改变时区
      const startTimeISO = startTimeInRuleTimezone.utc().toISOString();
      setPreviewDataResetKey(prev => prev + 1); // 标记预览数据已更新
      previewPeriodInstance({
        variables: {
          ruleId: selectedRuleId,
          periodStartTime: startTimeISO
        }
      }).catch((error) => {
        message.error('预览失败: ' + error.message);
      });
    } else if (addHistoricalModalVisible && (!selectedRuleId || !selectedStartTime)) {
      // 如果弹窗打开但没有选中规则或时间，重置预览数据
      setPreviewDataResetKey(0);
    }
  }, [addHistoricalModalVisible, selectedRuleId, selectedStartTime, selectedRule, previewPeriodInstance]);

  // 确认创建历史账期
  const handleConfirmCreateHistorical = async () => {
    if (!selectedRuleId || !selectedStartTime || !selectedRule) {
      message.warning('请选择规则和设置开始时间');
      return;
    }

    // 防止重复提交
    if (isCreatingHistorical) {
      message.warning('正在创建中，请勿重复提交');
      return;
    }

    setIsCreatingHistorical(true);
    try {
      // 将用户选择的时间理解为规则时区的本地时间，然后转换为 UTC 的 ISO 字符串
      const startTimeInRuleTimezone = selectedStartTime.tz(selectedRule.timeZone, true); // true 表示保持时间值不变，只改变时区
      const startTimeISO = startTimeInRuleTimezone.utc().toISOString();
      const result = await createHistoricalPeriodInstance({
        variables: {
          ruleId: selectedRuleId,
          periodStartTime: startTimeISO
        }
      });

      if (result.data?.createHistoricalPeriodInstance?.success) {
        message.success('历史账期实例创建成功，将在汇总时间到达后自动汇总');
        handleCloseAddHistorical();
        // 刷新页面数据
        if (viewRule?.id) {
          refetchInstances({ ruleId: viewRule.id });
        }
        refetchAllRules();
      } else {
        message.error(result.data?.createHistoricalPeriodInstance?.message || '创建历史账期实例失败');
      }
    } catch (error) {
      message.error('创建历史账期实例失败: ' + (error as Error).message);
    } finally {
      setIsCreatingHistorical(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <Card title="筛选" className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">客户类型</label>
            <Select
              value={clientRoleType}
              onChange={(value) => {
                setClientRoleType(value);
                setClientId(undefined);
                setViewRuleId(undefined);
                setPage(1);
              }}
              options={settlementClientRoleTypeOptions}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{clientLabel}</label>
            <Select
              value={clientId}
              onChange={(val) => {
                setClientId(val || undefined);
                setViewRuleId(undefined);
                setPage(1);
                if (val && billType) {
                  refetchRule({ clientId: val, billType });
                  refetchAllRules({ clientId: val, billType });
                }
              }}
              className="w-full"
              placeholder={`选择${clientLabel}`}
              options={settlementClientOptions}
              showSearch
              optionFilterProp="label"
              loading={settlementClientLoading}
              allowClear
            >
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">账单类型</label>
            <Select
              value={billType}
              onChange={(val) => {
                setBillType(val);
                setViewRuleId(undefined);
                setPage(1);
                if (clientId) {
                  refetchRule({ clientId, billType: val });
                  refetchAllRules({ clientId, billType: val });
                }
              }}
              className="w-full"
            >
              <Option value="DELIVERY_FEE_BILL">运费账单</Option>
              <Option value="SLA_BILL">SLA 账单</Option>
              <Option value="DSP_DELIVERY_FEE_BILL">DSP运费账单</Option>
              <Option value="SORTING_BILL">分拣账单</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">规则版本</label>
            <Select
              value={viewRule?.id}
              onChange={(val) => {
                setViewRuleId(val);
                setPage(1);
              }}
              className="w-full"
              placeholder="选择规则版本"
              disabled={sortedRules.length === 0}
              options={sortedRules.map((r: any) => ({
                value: r.id,
                label: `v${r.version}（${r.status === 1 ? '有效' : '无效'}）`,
              }))}
            />
          </div>
          <div className="flex items-end gap-2">
            {canOperate && (
              <Button
                type="default"
                icon={<PlusOutlined />}
                onClick={handleOpenAddHistorical}
                disabled={!clientId || !billType}
                title={!clientId || !billType ? '请先选择客户和账单类型' : '添加历史账期'}
                className="flex-1"
              >
                添加历史账期
              </Button>
            )}
            {canOperate && (
              <Button
                type="primary"
                onClick={handleGenerateBillingPeriod}
                disabled={!clientId || !billType || !rule}
                title={!rule ? '请先创建账期规则' : '根据当前规则刷新账期实例'}
                className="flex-1"
              >
                刷新账期实例
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card
        title={viewRule ? `账期规则（v${viewRule.version} · ${viewRule.status === 1 ? '有效' : '无效'}）` : '账期规则'}
        extra={canOperate && (
          <Button type="primary" icon={<PlusOutlined />} disabled={!clientId || !billType} onClick={handleOpenRuleVersion}>
            新增规则版本
          </Button>
        )}
      >
        {viewRule ? (
          <Descriptions column={2}>
            <Descriptions.Item label="版本">{viewRule.version}</Descriptions.Item>
            <Descriptions.Item label="周期类型">{viewRule.cycleType}</Descriptions.Item>
            <Descriptions.Item label="关账偏移/时间">{viewRule.closeOffsetDays} / {viewRule.closeTimeOfDay}</Descriptions.Item>
            <Descriptions.Item label="出账偏移/时间">{viewRule.issueOffsetDays} / {viewRule.issueTimeOfDay}</Descriptions.Item>
            <Descriptions.Item label="汇总偏移/时间">{viewRule.summaryOffsetDays} / {viewRule.summaryTimeOfDay}</Descriptions.Item>
            <Descriptions.Item label="时区">{viewRule.timeZone}</Descriptions.Item>
            <Descriptions.Item label="滚入">{viewRule.carryOverFlag ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="排除逻辑">{viewRule.exclusionLogic}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={viewRule.status === 1 ? 'green' : 'red'}>{viewRule.status === 1 ? '有效' : '无效'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="更新时间 (本地时区)">
              {viewRule.updatedAt ? new Date(viewRule.updatedAt).toLocaleString('zh-CN') : '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div className="text-gray-500">{clientId && billType ? '该客户与账单类型暂无账期规则' : '请选择客户与账单类型'}</div>
        )}
      </Card>

      <Card title="该规则生成的账期实例">
        <Table
          dataSource={instances}
          rowKey="id"
          pagination={false}
          className="mb-4"
          columns={[
            {
              title: '实例ID',
              dataIndex: 'id',
              key: 'id',
              width: 140,
              render: (id: string) => (
                <Button
                  type="link"
                  onClick={() => handleViewBill(id)}
                  className="p-0 h-auto"
                >
                  {id}
                </Button>
              )
            },
            {
              title: '账期开始',
              dataIndex: 'periodStart',
              key: 'periodStart',
              width: 180,
              render: (time: string, record: any) =>
                time && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : time || '-'
            },
            {
              title: '账期结束',
              dataIndex: 'periodEnd',
              key: 'periodEnd',
              width: 180,
              render: (time: string, record: any) =>
                time && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : time || '-'
            },
            {
              title: '关账时间',
              dataIndex: 'closeTime',
              key: 'closeTime',
              width: 180,
              // 后端对空值 toString 为 "null"
              render: (time: string, record: any) =>
                time && time !== 'null' && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : '-'
            },
            {
              title: '汇总时间',
              dataIndex: 'summaryTime',
              key: 'summaryTime',
              width: 180,
              render: (time: string, record: any) =>
                time && time !== 'null' && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : '-'
            },
            {
              title: '时区',
              dataIndex: 'timeZone',
              key: 'timeZone',
              width: 150
            },
            { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (s: number) => <Tag color={INSTANCE_STATUS_TAG[s]?.color ?? 'default'}>{INSTANCE_STATUS_TAG[s]?.label ?? s}</Tag> },
            { title: '是否出账', dataIndex: 'isIssued', key: 'isIssued', width: 120, render: (v: number) => <Tag color={v === 1 ? 'blue' : 'default'}>{v === 1 ? '已出账' : '未出账'}</Tag> },
            {
              title: '创建时间 (本地时区)',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 200,
              render: (time: string) => time ? new Date(time).toLocaleString('zh-CN') : '-'
            },
            {
              title: '操作',
              key: 'actions',
              width: 200,
              render: (_, record: any) => (
                <div className="space-x-2">
                  {canOperate && (
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDeleteInstance(record.id)}
                      disabled={record.isIssued === 1 || record.status === 3}
                      title={record.status === 3 ? '实例已作废' : record.isIssued === 1 ? '已出账的实例不能删除' : undefined}
                    >
                      删除
                    </Button>
                  )}
                  {canOperate && (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => handleRegenerateInstance(record.id)}
                      disabled={record.isIssued === 1 || !viewRuleIsActive}
                      title={!viewRuleIsActive ? '仅当前有效版本的实例可重新生成' : undefined}
                    >
                      重生成
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
          scroll={{ x: 1360 }}
        />
        <div className="mt-4 flex justify-end">
          <Pagination
            current={paginationInfo?.currentPage || 1}
            pageSize={paginationInfo?.pageSize || pageSize}
            total={paginationInfo?.totalElements || 0}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
          />
        </div>
      </Card>

      {/* 账单详情弹窗 */}
      <BillDetailModal
        visible={billModalVisible}
        periodInstanceId={selectedPeriodInstanceId}
        onClose={handleCloseBillModal}
      />

      {/* 添加历史账期弹窗 */}
      <Modal
        title="添加历史账期"
        open={addHistoricalModalVisible}
        onCancel={handleCloseAddHistorical}
        onOk={handleConfirmCreateHistorical}
        okText="确认创建"
        cancelText="取消"
        width={800}
        okButtonProps={{ 
          disabled: !selectedRuleId || !selectedStartTime || previewLoading || isCreatingHistorical,
          loading: isCreatingHistorical
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="选择历史规则" required>
            <Select
              placeholder="请选择账期规则"
              value={selectedRuleId}
              onChange={(value) => {
                setSelectedRuleId(value);
                form.setFieldsValue({ ruleId: value });
              }}
              className="w-full"
            >
              {allRules.map((r: any) => (
                <Option key={r.id} value={r.id}>
                  版本 {r.version} {r.status === 1 ? '(有效)' : '(无效)'} - {r.cycleType} - {r.timeZone}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="账期开始时间" required>
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              value={selectedStartTime}
              onChange={(value) => {
                setSelectedStartTime(value);
                form.setFieldsValue({ startTime: value });
              }}
              className="w-full"
              placeholder="选择账期开始时间"
            />
          </Form.Item>

          {selectedRule && (
            <Form.Item label="规则时区">
              <Input value={selectedRule.timeZone} disabled />
            </Form.Item>
          )}

          {previewLoading && (
            <div className="text-center py-4">
              <span>正在预览...</span>
            </div>
          )}

          {previewInstance && !previewLoading && (
            <Card title="预览账期实例" size="small" className="mt-4">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="账期开始">
                  {previewInstance.periodStart && previewInstance.timeZone
                    ? formatTimeWithTimezone(previewInstance.periodStart, previewInstance.timeZone)
                    : previewInstance.periodStart || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="账期结束">
                  {previewInstance.periodEnd && previewInstance.timeZone
                    ? formatTimeWithTimezone(previewInstance.periodEnd, previewInstance.timeZone)
                    : previewInstance.periodEnd || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="关账时间">
                  {previewInstance.closeTime && previewInstance.timeZone
                    ? formatTimeWithTimezone(previewInstance.closeTime, previewInstance.timeZone)
                    : previewInstance.closeTime || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="汇总时间">
                  {previewInstance.summaryTime && previewInstance.timeZone
                    ? formatTimeWithTimezone(previewInstance.summaryTime, previewInstance.timeZone)
                    : previewInstance.summaryTime || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="出账时间">
                  {previewInstance.issueTime && previewInstance.timeZone
                    ? formatTimeWithTimezone(previewInstance.issueTime, previewInstance.timeZone)
                    : previewInstance.issueTime || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="时区">
                  {previewInstance.timeZone || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Form>
      </Modal>

      {/* 新增账期规则版本弹窗 */}
      <Modal
        title="新增账期规则版本"
        open={ruleVersionModalVisible}
        onOk={handleSubmitRuleVersion}
        onCancel={() => setRuleVersionModalVisible(false)}
        okButtonProps={{ loading: isCreatingRuleVersion }}
        forceRender
      >
        <Form form={ruleVersionForm} layout="vertical">
          <Form.Item name="cycleType" label="周期类型" rules={[{ required: true }]}>
            <Select options={CYCLE_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }))} />
          </Form.Item>
          {([
            ['close', '关账'],
            ['summary', '汇总'],
            ['issue', '出账'],
          ] as const).map(([key, label]) => (
            <Space key={key} align="start">
              <Form.Item name={`${key}OffsetDays`} label={`${label}偏移天数（M+N）`} rules={[{ required: true }]}>
                <InputNumber min={0} max={365} precision={0} />
              </Form.Item>
              <Form.Item
                name={`${key}TimeOfDay`}
                label={`${label}时刻`}
                rules={[{ required: true }, { pattern: /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/, message: '格式 HH:mm:ss' }]}
              >
                <Input placeholder="HH:mm:ss" />
              </Form.Item>
            </Space>
          ))}
          <Form.Item name="timeZoneId" label="时区（IANA，如 Asia/Shanghai）" extra="留空则取结算客户时区">
            <Input allowClear />
          </Form.Item>
          <Form.Item name="carryOver" label="超期清分滚入下一账期" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BillingPeriodOverviewPage;